// Onglets « Identité et fondement » et « Conditions » de l'éditeur de version.
import { tousLesChamps, type ContenuVersion } from '@cc/contrats';
import { Bouton, Champ, GrilleChamps, Selection, ZoneTexte } from '@cc/ui';
import { Plus, Trash2 } from 'lucide-react';
import { LIBELLES_DOMAINES_DEMARCHE } from '../contenu';
import { EditeurCondition, texteCondition } from '../formulaire/EditeurCondition';
import styles from '../demarches.module.css';

export interface ProprietesOnglet {
  contenu: ContenuVersion;
  onChanger: (c: ContenuVersion) => void;
  lectureSeule: boolean;
}

export function OngletIdentite({ contenu, onChanger, lectureSeule }: ProprietesOnglet) {
  const maj = (patch: Partial<ContenuVersion>) => onChanger({ ...contenu, ...patch });
  const domaines = Object.keys(LIBELLES_DOMAINES_DEMARCHE).includes(contenu.domaine) ? LIBELLES_DOMAINES_DEMARCHE : { ...LIBELLES_DOMAINES_DEMARCHE, [contenu.domaine]: contenu.domaine };
  return (
    <div className={styles.pile}>
      <GrilleChamps>
        <Champ id="version-libelle" libelle="Libellé de la démarche" value={contenu.libelle} required disabled={lectureSeule} aide="Tel qu'il apparaît dans le catalogue public et sur les documents." onChange={(e) => maj({ libelle: e.target.value })} />
        <Selection id="version-domaine" libelle="Domaine" value={contenu.domaine} disabled={lectureSeule} options={Object.entries(domaines).map(([valeur, libelle]) => ({ valeur, libelle }))} onChange={(e) => maj({ domaine: e.target.value })} />
      </GrilleChamps>
      <ZoneTexte id="version-description" libelle="À quoi sert cette démarche" value={contenu.description} rows={3} disabled={lectureSeule} facultatif aide="Texte affiché à l'usager dans le catalogue." onChange={(e) => maj({ description: e.target.value })} />
      <Champ id="version-autorite" libelle="Autorité responsable" value={contenu.autorite_responsable} required disabled={lectureSeule} onChange={(e) => maj({ autorite_responsable: e.target.value })} />
      <ZoneTexte
        id="version-fondement"
        libelle="Fondement juridique"
        value={contenu.fondement}
        rows={3}
        required
        disabled={lectureSeule}
        aide="Obligatoire pour publier ; « à confirmer » est admis tant que l'administration n'a pas validé la référence (H3-05)."
        onChange={(e) => maj({ fondement: e.target.value })}
      />
    </div>
  );
}

export function OngletConditions({ contenu, onChanger, lectureSeule }: ProprietesOnglet) {
  const ce = contenu.conditions_eligibilite;
  const champs = tousLesChamps(contenu.formulaire).map((x) => x.champ);
  const maj = (patch: Partial<ContenuVersion['conditions_eligibilite']>) => onChanger({ ...contenu, conditions_eligibilite: { ...ce, ...patch } });
  const majRegle = (i: number, patch: Partial<ContenuVersion['conditions_eligibilite']['regles'][number]>) => maj({ regles: ce.regles.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  return (
    <div className={styles.pile}>
      <ZoneTexte id="version-conditions" libelle="Qui peut demander (texte affiché à l'usager)" value={ce.texte} rows={4} disabled={lectureSeule} facultatif onChange={(e) => maj({ texte: e.target.value })} />
      <section aria-labelledby="regles-titre" className={styles.pile}>
        <h3 id="regles-titre">Règles d'éligibilité évaluées au dépôt</h3>
        <p className={styles.aideDiscrete}>Chaque règle est une condition sur les champs du formulaire ; si elle n'est pas remplie, le dépôt est refusé avec le message indiqué. Ajoutez d'abord les champs concernés.</p>
        {ce.regles.length === 0 ? <p className={styles.aideDiscrete}>Aucune règle : toute demande complète est recevable.</p> : null}
        <ul className={styles.liste}>
          {ce.regles.map((r, i) => (
            <li key={i} className={styles.carte}>
              <div className={styles.carteEntete}>
                <h3>
                  <span className={styles.rang}>{i + 1}</span>
                  Règle : {texteCondition(r.condition, champs)}
                </h3>
                {!lectureSeule ? (
                  <Bouton petit variante="discret" icone={Trash2} aria-label={`Retirer la règle ${i + 1}`} onClick={() => maj({ regles: ce.regles.filter((_, j) => j !== i) })}>
                    Retirer
                  </Bouton>
                ) : null}
              </div>
              {!lectureSeule ? (
                <>
                  <EditeurCondition libelle="Condition à remplir" condition={r.condition} onChanger={(c) => majRegle(i, { condition: c ?? { champ: champs[0]?.code ?? '', operateur: 'renseigne' } })} champs={champs} />
                  <Champ libelle="Message affiché si la condition n'est pas remplie" value={r.message} required onChange={(e) => majRegle(i, { message: e.target.value })} />
                </>
              ) : (
                <p>Message : {r.message}</p>
              )}
            </li>
          ))}
        </ul>
        {!lectureSeule ? (
          <div>
            <Bouton variante="secondaire" icone={Plus} disabled={champs.length === 0} onClick={() => maj({ regles: [...ce.regles, { condition: { champ: champs[0]?.code ?? '', operateur: 'renseigne' }, message: 'Vous ne remplissez pas les conditions de cette démarche.' }] })}>
              Ajouter une règle
            </Bouton>
          </div>
        ) : null}
      </section>
    </div>
  );
}
